import { ModelSizeCutted } from "oc-jaco3d/arrangement/ModelSizeCutted";
import { v3 } from "oc-threejs/short3";
import { vector } from "oc-threejs/Maths3";
import { cloneDeep } from "oc-commons/Commons";
import * as Scenes from "oc-threejs/Scenes";
import { BoxGeometry, ExtrudeGeometry, Quaternion, Shape, Vector3 } from "three";

export type TMyModelCuttedClippingParameters = {
  size: Vector3;
  fixGridPosition: Vector3;
  diagonalCutted: boolean;
  rotateBeforeCutting: number;
  diagonalCuttingprops: {
    left: boolean;
    top: number;
    bottom: number;
  };
};

export function createDefaultClippingParamters(): TMyModelCuttedClippingParameters {
  return {
    size: v3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
    fixGridPosition: v3(0, 0, 0),
    rotateBeforeCutting: 0,
    diagonalCutted: false,
    diagonalCuttingprops: {
      left: false,
      top: Number.POSITIVE_INFINITY,
      bottom: Number.POSITIVE_INFINITY,
    },
  };
}

export class MyModelCutted extends ModelSizeCutted {
  defaultClippingParameters = createDefaultClippingParamters();
  // clippingParameters werden aufgrund der generischen Natur nicht automatisch persistiert - hierzu Model ableiten
  clippingParameters: TMyModelCuttedClippingParameters = cloneDeep(this.defaultClippingParameters);

  clippingFunction = (model, clipping) => {
    if (this.clippingParameters.rotateBeforeCutting) {
      const height = Scenes.boundingBoxLocalMatrix(model).getSize(v3()).y;
      const beta = Math.PI - this.clippingParameters.rotateBeforeCutting;
      const offset = height * Math.cos(beta);
      model.position.x += offset; //verschiebe rotationspunkt genau weit genug, damit die ecke abgeschnitten wird. (nach unten rotieren)
      model.quaternion.multiply(
        new Quaternion().setFromAxisAngle(vector.z, this.clippingParameters.rotateBeforeCutting)
      );
      Scenes.intersect(model, clipping);
      if (this.clippingParameters.rotateBeforeCutting > 0) {
        //nach oben gedreht
        const modelsize = Scenes.boundingBoxLocalMatrix(model).getSize(v3());
        model.position.y += modelsize.y / Math.tan(beta); //verschiebe um die unten liegende 3ecks höhe
      }
    } else {
      Scenes.intersect(model, clipping);
    }
  };

  isClipping(): boolean {
    return (
      this.clipping &&
      (super.isClipping() ||
        (this.clippingParameters.diagonalCutted &&
          Number.isFinite(this.clippingParameters.diagonalCuttingprops.top) &&
          Number.isFinite(this.clippingParameters.diagonalCuttingprops.bottom)) ||
        (this.clippingParameters.rotateBeforeCutting ?? 0) !== 0)
    );
  }

  getClippingGeometry() {
    return this.clippingParameters.diagonalCutted
      ? this.getDiagClippingGeometry()
      : this.clippingParameters.rotateBeforeCutting
        ? this.getRotatetedBoxClippingGeometry()
        : super.getClippingGeometry();
  }

  getRotatetedBoxClippingGeometry() {
    const s = this.getSizeCutted(0.0001);
    s.y += 10; //y nicht abschneiden!
    const g = new BoxGeometry(s.x, s.y, s.z);

    // BoxBufferGeometry erzeugt pro Material einen MaterialIndex. Damit die sich nicht bei der
    // Intersection übertragen, wird hiermit der MaterialIndex überall auf 0 gesetzt:
    g.groups = [];

    const uncuttedBox = this.getBoxUnclipped();

    const fixGP = v3(this.clippingParameters.fixGridPosition);
    fixGP.y = 0.5; // nach oben oder nach unten kippen ist uns egal
    const middleCutted = s.clone().multiplyScalar(0.5);
    const fixCutted = s.clone().multiply(fixGP);
    const fixUncutted = uncuttedBox.grid(fixGP);
    const t = fixUncutted.clone().sub(fixCutted).add(middleCutted);
    g.translate(t.x, t.y, t.z);
    return g;
  }

  getDiagClippingGeometry() {
    const {
      left,
      top: topLength,
      bottom: bottomLength,
    } = this.clippingParameters.diagonalCuttingprops;
    const s = this.getSizeCutted();

    const shape = new Shape();
    if (left) {
      shape.moveTo(s.x / 2, -s.y / 2);
      shape.lineTo(s.x / 2, s.y / 2);
      shape.lineTo(s.x / 2 - topLength, s.y / 2);
      shape.lineTo(s.x / 2 - bottomLength, -s.y / 2);
      shape.lineTo(s.x / 2, -s.y / 2);
    } else {
      shape.moveTo(-s.x / 2, -s.y / 2);
      shape.lineTo(-s.x / 2 + bottomLength, -s.y / 2);
      shape.lineTo(-s.x / 2 + topLength, s.y / 2);
      shape.lineTo(-s.x / 2, s.y / 2);
      shape.lineTo(-s.x / 2, -s.y / 2);
    }

    const extrudeSettings = {
      steps: 1,
      depth: s.z,
      bevelEnabled: false,
    };
    const geo = new ExtrudeGeometry(shape, extrudeSettings);
    geo.translate(0, 0, -s.z / 2);

    geo.groups = [];

    const uncuttedBox = this.getBoxUnclipped();

    const fixGP = this.clippingParameters.fixGridPosition;
    const middleCutted = s.clone().multiplyScalar(0.5);
    const fixCutted = s.clone().multiply(fixGP);
    const fixUncutted = uncuttedBox.grid(fixGP);
    const t = fixUncutted.clone().sub(fixCutted).add(middleCutted);
    geo.translate(t.x, t.y, t.z);
    return geo;
  }
}
